/* Interactive fragrance wheel (香氣輪) — v2
   Reads inline JSON emitted by sections/fragrance-wheel.liquid and renders
   an SVG sunburst.
   Full view: poster-style equal wedges (name + character line + example oils)
   with a thin decorative tick rim and quadrant labels around the outside.
   Desktop: hovering wedges/oils fills the side info panel; family zoom keeps
   the radial fan. Mobile (<720px) zoom: family bar + card carousel on top,
   half-fan wheel cropped at the bottom, synced to card swipes.
   No external libraries. */

const SVG_NS = 'http://www.w3.org/2000/svg';
const VB = 1000;
const C = VB / 2;

/* Full view radii — poster wedges + thin tick rim + quadrant text */
const FAM_R0 = 150;
const FAM_R1 = 436;
const RIM_R0 = 446;
const RIM_R1 = 460;
const QUAD_R = 476;
const QUAD_R_FLIP = 498;
const FAMILY_FS = 26;

/* Family (zoomed) view radii — desktop fan */
const DESC_R0 = 178;
const DESC_R1 = 316;
const NAME_R0 = 320;
const NAME_R1 = 478;

/* Family (zoomed) view — mobile half-fan "bowl": the bottom half of the wheel
   hangs beneath the cards, wedges fanning downward. The active wedge sits at
   6 o'clock under the pointer and grows OUTWARD on focus (inner radius fixed,
   so the hub stays a clean unified circle). */
const ROTOR_R0 = 135;
const ROTOR_R1 = 262;
const ROTOR_GROW = 18;
const FULL_VIEWBOX = '-30 -30 1060 1060';
const FAN_VB = { x: 196, y: 560, w: 608, h: 228 };
const FAN_VIEWBOX = `${FAN_VB.x} ${FAN_VB.y} ${FAN_VB.w} ${FAN_VB.h}`;

const FAMILY_GAP_DEG = 1.1;
const MAX_ITEM_DEG = 18;

function polar(r, deg) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: C + r * Math.cos(rad), y: C + r * Math.sin(rad) };
}

function arcPath(r0, r1, a0, a1) {
  const large = a1 - a0 > 180 ? 1 : 0;
  const p1 = polar(r1, a0);
  const p2 = polar(r1, a1);
  const p3 = polar(r0, a1);
  const p4 = polar(r0, a0);
  return [
    'M', p1.x, p1.y,
    'A', r1, r1, 0, large, 1, p2.x, p2.y,
    'L', p3.x, p3.y,
    'A', r0, r0, 0, large, 0, p4.x, p4.y,
    'Z',
  ].join(' ');
}

function el(name, attrs) {
  const node = document.createElementNS(SVG_NS, name);
  if (attrs) for (const k in attrs) node.setAttribute(k, attrs[k]);
  return node;
}

/* Lighten (f > 0) or darken (f < 0) a hex color */
function shade(hex, f) {
  const m = /^#?([0-9a-f]{6})/i.exec(hex || '#999999');
  const n = parseInt(m ? m[1] : '999999', 16);
  const ch = (shift) => {
    let v = (n >> shift) & 255;
    v = f >= 0 ? v + (255 - v) * f : v * (1 + f);
    return Math.round(Math.min(255, Math.max(0, v)));
  };
  return `rgb(${ch(16)} ${ch(8)} ${ch(0)})`;
}

function textWidth(str, fs) {
  let w = 0;
  for (const chr of str) w += chr.codePointAt(0) > 0x2e80 ? fs : fs * 0.58;
  return w;
}

function truncate(str, fs, maxW) {
  if (textWidth(str, fs) <= maxW) return str;
  let out = '';
  let w = 0;
  for (const chr of str) {
    const cw = chr.codePointAt(0) > 0x2e80 ? fs : fs * 0.58;
    if (w + cw + fs > maxW) break;
    out += chr;
    w += cw;
  }
  return out + '…';
}

/* Greedy split into at most two lines that each fit maxW */
function splitLines(str, fs, maxW) {
  if (textWidth(str, fs) <= maxW) return [str];
  let line1 = '';
  let w = 0;
  let rest = '';
  for (const chr of str) {
    const cw = chr.codePointAt(0) > 0x2e80 ? fs : fs * 0.58;
    if (!rest && w + cw <= maxW) {
      line1 += chr;
      w += cw;
    } else {
      rest += chr;
    }
  }
  return [line1, truncate(rest, fs, maxW)];
}

/* Radial label; flipped on the left half so nothing reads upside down */
function radialText(midDeg, rIn, rOut, fs, str, opts = {}) {
  const deg = ((midDeg % 360) + 360) % 360;
  const pad = opts.pad != null ? opts.pad : 10;
  const right = deg < 180;
  const p = right ? polar(rIn + pad, deg) : polar(rOut - pad, deg);
  const rot = right ? deg - 90 : deg + 90;
  const t = el('text', {
    x: p.x,
    y: p.y,
    class: 'fw-label',
    'font-size': fs,
    'text-anchor': 'start',
    'dominant-baseline': 'central',
    transform: `rotate(${rot} ${p.x} ${p.y})`,
  });
  if (opts.opacity) t.setAttribute('opacity', opts.opacity);
  t.textContent = truncate(str, fs, rOut - rIn - pad * 2);
  return t;
}

class FragranceWheel extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-fragrance-wheel-data]');
    if (!dataEl) return;
    try {
      this.data = JSON.parse(dataEl.textContent);
    } catch (err) {
      console.error('[fragrance-wheel] bad data JSON', err);
      return;
    }
    /* keep empty families visible on the wheel (e.g. upcoming ranges);
       their wedge links to the collection instead of zooming */
    this.data.families = (this.data.families || []).filter((f) => f.items);
    this.svg = this.querySelector('.fragrance-wheel__svg');
    this.center = this.querySelector('[data-fw-center]');
    this.panel = this.querySelector('[data-fw-panel]');
    this.panelDefault = this.querySelector('[data-fw-panel-default]');
    this.panelDetail = this.querySelector('[data-fw-panel-detail]');
    this._restoreTimer = null;
    this._uid = Math.random().toString(36).slice(2, 8);

    /* Two-column hover experience only when there's a mouse AND room; every
       other case (touch, or a narrow window) gets the card/fan experience.
       Keying on capability + width — not width alone — removes the awkward
       720–990px zone where a stacked panel + hover caused a flicker loop. */
    this._desktopMq = window.matchMedia('(min-width: 990px) and (pointer: fine)');
    const onMq = () => this.render(this.view || 'all', false);
    if (this._desktopMq.addEventListener) this._desktopMq.addEventListener('change', onMq);
    else this._desktopMq.addListener(onMq);

    this._buildMobileEls();

    if (this.panel) {
      this.panel.addEventListener('pointerenter', () => this._panelCancelRestore());
      this.panel.addEventListener('pointerleave', (e) => {
        if (e.pointerType === 'mouse') this._panelScheduleRestore();
      });
    }
    window.addEventListener('popstate', () => this._fromHash());

    this._fromHash();
  }

  get isMobile() {
    return !this._desktopMq.matches;
  }

  _fromHash() {
    const m = /fw=([^&]+)/.exec(location.hash);
    const id = m ? decodeURIComponent(m[1]) : null;
    if (id && this.data.families.some((f) => f.id === id)) this.render(id, false);
    else this.render('all', false);
  }

  show(view) {
    this.render(view, true);
  }

  render(view, pushHash) {
    this.view = view;
    if (pushHash) {
      const url = view === 'all' ? '#' : `#fw=${encodeURIComponent(view)}`;
      history.pushState(null, '', url === '#' ? location.pathname + location.search : url);
    }
    this.svg.classList.add('is-entering');
    this.svg.replaceChildren();
    this.svg.setAttribute('viewBox', FULL_VIEWBOX);
    this.center.replaceChildren();
    this.classList.remove('fragrance-wheel--fanmode');
    this.fambarEl.classList.remove('is-active');
    this.carouselEl.classList.remove('is-active');
    this.fanNavEl.classList.remove('is-active');
    this._rotorGroups = null;
    this._panelRestore();
    if (view === 'all') this._renderAll();
    else if (this.isMobile) this._renderFamilyMobile(view);
    else this._renderFamily(view);
    requestAnimationFrame(() =>
      requestAnimationFrame(() => this.svg.classList.remove('is-entering'))
    );
  }

  /* ---------- full wheel (poster style) ---------- */

  _examplesFor(fam, max) {
    if (fam.ex && fam.ex.trim()) return fam.ex.trim();
    return fam.items
      .slice(0, max)
      .map((it) => it.t)
      .join('、');
  }

  /* One line of a wedge's text block: anchored at the wedge centroid and
     offset vertically by dy, so lines stack the same way at every angle
     (radius-offset lines collide on side wedges). Long text (e.g. English)
     first shrinks to fit — down to 70% — then truncates. */
  _wedgeLine(mid, r, dy, fs, str, opts = {}) {
    const maxW = 2 * r * Math.sin(((this._famPer / 2) * Math.PI) / 180) * 0.95;
    const w = textWidth(str, fs);
    if (w > maxW) fs = Math.max(fs * 0.7, (fs * maxW) / w);
    const p = polar(r, mid);
    const t = el('text', {
      x: p.x,
      y: p.y + dy,
      class: opts.cls || 'fw-label fw-label--dim',
      'font-size': fs,
      'text-anchor': 'middle',
      'dominant-baseline': 'central',
    });
    if (opts.opacity) t.setAttribute('opacity', opts.opacity);
    t.textContent = truncate(str, fs, maxW);
    return t;
  }

  _renderAll() {
    const fams = this.data.families;
    const ui = this.data.ui;
    const n = fams.length;
    const per = 360 / n;
    this._famPer = per;
    const g = FAMILY_GAP_DEG / 2;
    const mobile = this.isMobile;

    fams.forEach((fam, fi) => {
      const a0 = fi * per + g;
      const a1 = (fi + 1) * per - g;
      const mid = (a0 + a1) / 2;

      /* family wedge */
      const wedge = el('path', {
        d: arcPath(FAM_R0, FAM_R1, a0, a1),
        fill: fam.color,
        class: 'fw-family-wedge',
        role: 'button',
        tabindex: '0',
        'aria-label': `${fam.name}，${fam.items.length} ${ui.itemsSuffix}`,
      });
      const activate = () => {
        if (fam.items.length) this.show(fam.id);
        else if (fam.url) location.assign(fam.url);
      };
      wedge.addEventListener('click', activate);
      wedge.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          activate();
        }
      });
      if (!mobile) {
        wedge.addEventListener('pointerenter', (e) => {
          if (e.pointerType !== 'mouse') return;
          this._panelCancelRestore();
          this._panelFamily(fam);
        });
        wedge.addEventListener('pointerleave', (e) => {
          if (e.pointerType === 'mouse') this._panelScheduleRestore();
        });
      }
      this.svg.appendChild(wedge);

      /* wedge text — desktop: name / character line / oil count stacked as
         one block at the centroid (example oil names live in the hover
         panel); mobile: name only */
      if (mobile) {
        this.svg.appendChild(
          this._wedgeLine(mid, 293, 0, 28, fam.name, { cls: 'fw-label fw-label--family' })
        );
      } else {
        this.svg.appendChild(
          this._wedgeLine(mid, 295, -34, FAMILY_FS, fam.name, {
            cls: 'fw-label fw-label--family',
          })
        );
        if (fam.dsc && fam.dsc.trim()) {
          this.svg.appendChild(
            this._wedgeLine(mid, 295, 8, 13.5, fam.dsc.trim(), {
              cls: 'fw-label',
              opacity: 0.95,
            })
          );
        }
        if (fam.items.length) {
          this.svg.appendChild(
            this._wedgeLine(mid, 295, 38, 12, `${fam.items.length} ${ui.itemsSuffix}`, {
              opacity: 0.7,
            })
          );
        }
      }

      /* thin decorative tick rim — one tick per oil */
      if (ui.showRim) {
        const rim = el('g', { class: 'fw-rim' });
        const inner0 = a0 + 0.3;
        const inner1 = a1 - 0.3;
        const count = fam.items.length;
        const tickPer = (inner1 - inner0) / count;
        const tickW = Math.min(tickPer * 0.6, 4);
        for (let i = 0; i < count; i++) {
          const cMid = inner0 + (i + 0.5) * tickPer;
          rim.appendChild(
            el('path', {
              d: arcPath(RIM_R0, RIM_R1, cMid - tickW / 2, cMid + tickW / 2),
              fill: shade(fam.color, i % 2 === 0 ? 0.4 : 0.22),
            })
          );
        }
        this.svg.appendChild(rim);
      }
    });

    /* quadrant labels around the outside */
    (ui.quadrants || []).forEach((label, qi) => {
      if (label && label.trim()) this._quadrant(qi, label.trim());
    });

    /* center: title + intro (intro hidden on mobile via CSS) + hint (mobile via CSS) */
    if (ui.centerTitle && ui.centerTitle.trim()) {
      const title = document.createElement('p');
      title.className = 'fw-center__title';
      title.textContent = ui.centerTitle;
      this.center.appendChild(title);
    }
    if (ui.centerIntro && ui.centerIntro.trim()) {
      const intro = document.createElement('p');
      intro.className = 'fw-center__intro';
      intro.textContent = ui.centerIntro;
      this.center.appendChild(intro);
    }
    const hint = document.createElement('p');
    hint.className = 'fw-center__hint';
    hint.textContent = ui.hint;
    this.center.appendChild(hint);
  }

  _quadrant(qi, label) {
    const centers = [0, 90, 180, 270];
    const centerDeg = centers[qi];
    const flip = qi === 2;
    const r = flip ? QUAD_R_FLIP : QUAD_R;
    const start = centerDeg - 55;
    const end = centerDeg + 55;
    const id = `fwq-${this._uid}-${qi}`;

    const pA = flip ? polar(r, end) : polar(r, start);
    const pB = flip ? polar(r, start) : polar(r, end);
    const sweep = flip ? 0 : 1;
    const path = el('path', {
      id,
      d: `M ${pA.x} ${pA.y} A ${r} ${r} 0 0 ${sweep} ${pB.x} ${pB.y}`,
      fill: 'none',
    });
    this.svg.appendChild(path);

    const text = el('text', { class: 'fw-quad' });
    const tp = el('textPath', { startOffset: '50%', 'text-anchor': 'middle' });
    tp.setAttribute('href', `#${id}`);
    tp.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', `#${id}`);
    tp.textContent = label;
    text.appendChild(tp);
    this.svg.appendChild(text);
  }

  /* ---------- info panel (desktop hover) ---------- */

  _panelClear() {
    if (!this.panelDetail) return null;
    this.panelDetail.replaceChildren();
    return this.panelDetail;
  }

  _panelActivate() {
    if (!this.panelDetail) return;
    this.panelDefault.hidden = true;
    this.panelDetail.hidden = false;
  }

  _panelRestore() {
    this._panelCancelRestore();
    if (!this.panelDetail) return;
    this.panelDetail.hidden = true;
    this.panelDefault.hidden = false;
  }

  _panelScheduleRestore() {
    this._panelCancelRestore();
    this._restoreTimer = setTimeout(() => this._panelRestore(), 260);
  }

  _panelCancelRestore() {
    if (this._restoreTimer) {
      clearTimeout(this._restoreTimer);
      this._restoreTimer = null;
    }
  }

  _panelFamily(fam) {
    const d = this._panelClear();
    if (!d) return;
    const ui = this.data.ui;

    const title = document.createElement('h3');
    title.className = 'fw-panel__title';
    title.textContent = fam.name;
    title.style.color = fam.color;
    d.appendChild(title);

    if (fam.dsc && fam.dsc.trim()) {
      const sub = document.createElement('p');
      sub.className = 'fw-panel__sub';
      sub.textContent = fam.dsc;
      d.appendChild(sub);
    }

    const ex = this._examplesFor(fam, 6);
    if (ex) {
      const text = document.createElement('p');
      text.className = 'fw-panel__text';
      text.textContent = ex;
      d.appendChild(text);
    }

    if (fam.url) {
      const actions = document.createElement('div');
      actions.className = 'fw-panel__actions';
      const link = document.createElement('a');
      link.className = 'fw-panel__btn fw-panel__btn--ghost';
      link.href = fam.url;
      link.textContent = this._collectionLabel(fam);
      actions.appendChild(link);
      d.appendChild(actions);
    }

    this._panelActivate();
  }

  _panelItem(item, fam) {
    const d = this._panelClear();
    if (!d) return;
    const ui = this.data.ui;

    if (item.img) {
      const media = document.createElement('div');
      media.className = 'fw-panel__media';
      const img = document.createElement('img');
      img.loading = 'lazy';
      img.src = item.img;
      img.alt = item.t;
      img.width = 480;
      img.height = 270;
      media.appendChild(img);
      d.appendChild(media);
    }

    const head = document.createElement('div');
    head.className = 'fw-panel__head';
    const title = document.createElement('h3');
    title.className = 'fw-panel__title';
    title.textContent = item.t;
    head.appendChild(title);
    const noteInfo = item.n ? ui.notes[item.n] : null;
    if (noteInfo) {
      const chip = document.createElement('span');
      chip.className = 'fw-note-chip';
      chip.textContent = `${noteInfo.code} · ${noteInfo.label}`;
      chip.style.background = fam ? shade(fam.color, 0.75) : '';
      head.appendChild(chip);
    }
    d.appendChild(head);

    if (item.d) {
      const sub = document.createElement('p');
      sub.className = 'fw-panel__sub';
      sub.textContent = item.d;
      d.appendChild(sub);
    }
    if (item.s) {
      const text = document.createElement('p');
      text.className = 'fw-panel__text';
      text.textContent = item.s;
      d.appendChild(text);
    }

    const actions = document.createElement('div');
    actions.className = 'fw-panel__actions';
    if (item.p) {
      const price = document.createElement('span');
      price.className = 'fw-panel__price';
      price.textContent = item.p;
      actions.appendChild(price);
    }
    if (item.u) {
      const link = document.createElement('a');
      link.className = 'fw-panel__btn';
      link.href = item.u;
      link.textContent = ui.viewProduct;
      actions.appendChild(link);
    }
    if (actions.childNodes.length) d.appendChild(actions);

    this._panelActivate();
  }

  /* ---------- shared zoom center (desktop) ---------- */

  /* e.g. 探索全部 15 款香氣 — reads as "this family", not the whole site */
  _collectionLabel(fam) {
    const ui = this.data.ui;
    return fam.items.length
      ? `${ui.viewCollection} ${fam.items.length} ${ui.itemsSuffix}`
      : ui.viewCollection;
  }

  _centerFamilyInfo(fam, n) {
    const name = document.createElement('p');
    name.className = 'fw-center__family';
    name.textContent = fam.name;
    name.style.color = fam.color;

    this.center.append(name);

    if (fam.url) {
      const link = document.createElement('a');
      link.className = 'fw-center__link';
      link.href = fam.url;
      link.textContent = this._collectionLabel(fam);
      this.center.appendChild(link);
    }

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'fw-center__back';
    back.textContent = this.data.ui.back;
    back.addEventListener('click', () => this.show('all'));
    this.center.appendChild(back);
  }

  /* ---------- zoomed family (desktop fan) ---------- */

  _renderFamily(id) {
    const fam = this.data.families.find((f) => f.id === id);
    if (!fam || !fam.items.length) return this._renderAll();
    const n = fam.items.length;
    const per = Math.min(MAX_ITEM_DEG, 360 / n);
    const start = -(per * n) / 2;
    const arcAtDesc = (per * Math.PI * DESC_R0) / 180;
    const arcAtName = (per * Math.PI * NAME_R0) / 180;
    const fsDesc = Math.max(9, Math.min(12.5, arcAtDesc * 0.55));
    const fsName = Math.max(9.5, Math.min(15.5, arcAtName * 0.5));
    const gap = Math.min(0.3, per * 0.08);

    fam.items.forEach((item, i) => {
      const a0 = start + i * per + gap;
      const a1 = start + (i + 1) * per - gap;
      const mid = (a0 + a1) / 2;
      const group = el('g', { class: 'fw-seg' });
      group.appendChild(
        el('path', {
          d: arcPath(DESC_R0, DESC_R1, a0, a1),
          fill: shade(fam.color, i % 2 === 0 ? 0.16 : 0.04),
        })
      );
      group.appendChild(
        el('path', {
          d: arcPath(NAME_R0, NAME_R1, a0, a1),
          fill: shade(fam.color, i % 2 === 0 ? 0.38 : 0.26),
        })
      );
      if (item.d) {
        group.appendChild(radialText(mid, DESC_R0, DESC_R1, fsDesc, item.d, { opacity: 0.95 }));
      }
      const noteCode =
        item.n && this.data.ui.notes[item.n] ? `  ${this.data.ui.notes[item.n].code}` : '';
      group.appendChild(radialText(mid, NAME_R0, NAME_R1, fsName, item.t + noteCode));
      this._wireItem(group, item, fam);
      this.svg.appendChild(this._linkWrap(group, item));
    });

    this._centerFamilyInfo(fam, n);
  }

  _linkWrap(node, item) {
    if (!item.u) return node;
    const a = el('a');
    a.setAttribute('href', item.u);
    a.setAttribute('aria-label', item.d ? `${item.t}，${item.d}` : item.t);
    a.appendChild(node);
    return a;
  }

  _wireItem(node, item, fam) {
    node.addEventListener('pointerenter', (e) => {
      if (e.pointerType !== 'mouse') return;
      this._panelCancelRestore();
      this._panelItem(item, fam);
    });
    node.addEventListener('pointerleave', (e) => {
      if (e.pointerType === 'mouse') this._panelScheduleRestore();
    });
    node.addEventListener('click', (e) => {
      if (!item.u) e.preventDefault();
    });
  }

  /* ---------- zoomed family (mobile half-fan + card carousel) ---------- */

  _buildMobileEls() {
    const stage = this.querySelector('.fragrance-wheel__stage');

    this.fambarEl = document.createElement('div');
    this.fambarEl.className = 'fw-fambar';

    this.carouselEl = document.createElement('div');
    this.carouselEl.className = 'fw-carousel';

    this.fanNavEl = document.createElement('div');
    this.fanNavEl.className = 'fw-fannav';

    stage.after(this.fambarEl, this.carouselEl, this.fanNavEl);

    let ticking = false;
    this.carouselEl.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        this._syncFromScroll();
      });
    });
    window.addEventListener('resize', () => {
      if (this._rotorGroups) this._measureCarousel();
    });

    /* drag the bowl itself to spin it — drives the carousel scroll, which in
       turn rotates the rotor (single source of truth = card scroll position) */
    stage.addEventListener('pointerdown', (e) => {
      if (!this._rotorGroups) return;
      this._dragging = true;
      this._dragMoved = false;
      this._prevAngle = this._angleAt(e.clientX, e.clientY);
      this.carouselEl.style.scrollSnapType = 'none';
      try {
        stage.setPointerCapture(e.pointerId);
      } catch (err) {}
    });
    stage.addEventListener('pointermove', (e) => {
      if (!this._dragging || !this._cardStep) return;
      const a = this._angleAt(e.clientX, e.clientY);
      let d = a - this._prevAngle;
      if (d > 180) d -= 360;
      else if (d < -180) d += 360;
      this._prevAngle = a;
      if (Math.abs(d) > 0.6) this._dragMoved = true;
      this.carouselEl.scrollLeft += (d / this._per) * this._cardStep;
    });
    const endDrag = (e) => {
      if (!this._dragging) return;
      this._dragging = false;
      this.carouselEl.style.scrollSnapType = '';
      try {
        stage.releasePointerCapture(e.pointerId);
      } catch (err) {}
      this._scrollToCard(this._activeIdx); /* snap to the centred wedge */
    };
    stage.addEventListener('pointerup', endDrag);
    stage.addEventListener('pointercancel', endDrag);
  }

  /* screen-space angle of a point around the wheel centre (off-screen above
     the crop in fan mode) */
  _angleAt(clientX, clientY) {
    const r = this.svg.getBoundingClientRect();
    const cx = r.left + ((500 - FAN_VB.x) / FAN_VB.w) * r.width;
    const cy = r.top + ((500 - FAN_VB.y) / FAN_VB.h) * r.height;
    return (Math.atan2(clientY - cy, clientX - cx) * 180) / Math.PI;
  }

  _renderFamilyMobile(id) {
    const fam = this.data.families.find((f) => f.id === id);
    if (!fam || !fam.items.length) return this._renderAll();
    const ui = this.data.ui;
    const n = fam.items.length;
    const per = 360 / n;
    this._fam = fam;
    this._per = per;
    this._activeIdx = -1;

    this.classList.add('fragrance-wheel--fanmode');
    this.svg.setAttribute('viewBox', FAN_VIEWBOX);

    const rotor = el('g', { class: 'fw-rotor' });
    this.svg.appendChild(rotor);
    /* wedges laid out counter-clockwise (i grows leftward) so swiping the
       cards forward rotates the bowl in the chosen direction */
    const gap = Math.min(1.2, per * 0.06);
    const half = per / 2 - gap;
    this._rotorGroups = fam.items.map((item, i) => {
      const ctr = -(i + 0.5) * per;
      const a0 = ctr - half;
      const a1 = ctr + half;
      const g = el('g');
      const path = el('path', {
        d: arcPath(ROTOR_R0, ROTOR_R1, a0, a1),
        fill: shade(fam.color, i % 2 === 0 ? 0.3 : 0.12),
        class: 'fw-seg',
      });
      path.addEventListener('click', () => {
        if (!this._dragMoved) this._scrollToCard(i);
      });
      g.appendChild(path);
      rotor.appendChild(g);
      return { g, path, a0, a1 };
    });
    this._rotor = rotor;

    /* pointer at the bottom tip, pointing up into the active wedge */
    this.svg.appendChild(el('polygon', { points: '478,786 522,786 500,754', class: 'fw-pointer' }));

    /* family bar above the cards */
    this.fambarEl.replaceChildren();
    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'fw-fambar__back';
    back.textContent = ui.back;
    back.addEventListener('click', () => this.show('all'));
    const name = document.createElement('span');
    name.className = 'fw-fambar__name';
    name.textContent = fam.name;
    name.style.color = fam.color;
    this.fambarEl.append(back, name);
    if (fam.url) {
      const link = document.createElement('a');
      link.className = 'fw-fambar__link';
      link.href = fam.url;
      link.textContent = this._collectionLabel(fam);
      this.fambarEl.appendChild(link);
    }
    this.fambarEl.classList.add('is-active');

    this.carouselEl.classList.add('is-active');
    this.fanNavEl.classList.add('is-active');
    this._buildCards(fam);
    /* open on the middle oil so cards peek on BOTH sides — signals there's
       more to swipe to, left and right */
    this._initialIdx = Math.floor((n - 1) / 2);
    this.carouselEl.scrollLeft = 0;
    requestAnimationFrame(() => {
      this._measureCarousel();
      this.carouselEl.scrollLeft =
        this._cardBase + this._initialIdx * this._cardStep - this._centerOff;
      this._applyFraction(this._initialIdx);
    });
  }

  _buildCards(fam) {
    this.carouselEl.replaceChildren();
    fam.items.forEach((item) => {
      const card = document.createElement('div');
      card.className = 'fw-card';

      const media = document.createElement('div');
      media.className = 'fw-card__media';
      if (item.img) {
        const img = document.createElement('img');
        img.loading = 'lazy';
        img.src = item.img;
        img.alt = item.t;
        img.width = 480;
        img.height = 270;
        media.appendChild(img);
      } else {
        media.style.background = shade(fam.color, 0.6);
      }

      const body = document.createElement('div');
      body.className = 'fw-card__body';

      const head = document.createElement('div');
      head.className = 'fw-card__head';
      const title = document.createElement('p');
      title.className = 'fw-card__title';
      title.textContent = item.t;
      head.appendChild(title);
      const noteInfo = item.n ? this.data.ui.notes[item.n] : null;
      if (noteInfo) {
        const chip = document.createElement('span');
        chip.className = 'fw-note-chip';
        chip.textContent = noteInfo.code;
        head.appendChild(chip);
      }
      body.appendChild(head);

      if (item.d) {
        const desc = document.createElement('p');
        desc.className = 'fw-card__desc';
        desc.textContent = item.d;
        body.appendChild(desc);
      }

      const foot = document.createElement('div');
      foot.className = 'fw-card__foot';
      const price = document.createElement('span');
      price.className = 'fw-card__price';
      price.textContent = item.p || '';
      foot.appendChild(price);
      if (item.u) {
        const a = document.createElement('a');
        a.className = 'fw-card__btn';
        a.href = item.u;
        a.textContent = this.data.ui.viewProduct;
        foot.appendChild(a);
      }
      body.appendChild(foot);

      card.append(media, body);
      this.carouselEl.appendChild(card);
    });
    this._cards = Array.from(this.carouselEl.children);
  }

  _measureCarousel() {
    const cards = this._cards;
    if (!cards || !cards.length) return;
    this._cardBase = cards[0].offsetLeft;
    this._cardStep =
      cards.length > 1 ? cards[1].offsetLeft - cards[0].offsetLeft : cards[0].offsetWidth + 12;
    this._centerOff = (this.carouselEl.clientWidth - cards[0].offsetWidth) / 2;
  }

  _syncFromScroll() {
    if (!this._rotorGroups || !this._cardStep) return;
    const frac =
      (this.carouselEl.scrollLeft - this._cardBase + this._centerOff) / this._cardStep;
    this._applyFraction(frac);
  }

  _applyFraction(frac) {
    if (!this._rotorGroups) return;
    const n = this._fam.items.length;
    const clamped = Math.max(0, Math.min(n - 1, frac));
    /* bring the active wedge's centre to 6 o'clock (180°), under the pointer.
       At rest, settle exactly on the nearest wedge — scroll-snap can leave a
       sub-pixel offset that visibly rotates the wedge off the pointer. */
    const near = Math.round(clamped);
    const rot = !this._dragging && Math.abs(clamped - near) < 0.06 ? near : clamped;
    this._rotor.setAttribute(
      'transform',
      `rotate(${180 + (rot + 0.5) * this._per} 500 500)`
    );
    this._rotorGroups.forEach((s, i) => {
      const dist = Math.min(Math.abs(i - clamped), n - Math.abs(i - clamped));
      const focus = Math.max(0, 1 - dist / 0.85);
      /* grow the outer radius on focus; inner radius stays fixed (unified circle) */
      s.path.setAttribute('d', arcPath(ROTOR_R0, ROTOR_R1 + focus * ROTOR_GROW, s.a0, s.a1));
      s.g.style.opacity = String(0.5 + 0.5 * focus);
    });
    const active = near % n;
    if (active !== this._activeIdx) {
      this._activeIdx = active;
      this._updateFanNav(active);
    }
  }

  _updateFanNav(active) {
    if (!this.fanNavEl) return;
    const items = this._fam.items;
    this.fanNavEl.replaceChildren();
    const makeBtn = (idx, label, cls) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = `fw-fannav__btn ${cls}`;
      b.textContent = label;
      b.addEventListener('click', () => this._scrollToCard(idx));
      return b;
    };
    if (active > 0) {
      this.fanNavEl.appendChild(makeBtn(active - 1, `‹ ${items[active - 1].t}`, 'fw-fannav__prev'));
    }
    if (active < items.length - 1) {
      this.fanNavEl.appendChild(makeBtn(active + 1, `${items[active + 1].t} ›`, 'fw-fannav__next'));
    }
  }

  _scrollToCard(i) {
    if (!this._cardStep) this._measureCarousel();
    this.carouselEl.scrollTo({
      left: this._cardBase + i * this._cardStep - this._centerOff,
      behavior: 'smooth',
    });
  }
}

if (!customElements.get('fragrance-wheel')) {
  customElements.define('fragrance-wheel', FragranceWheel);
}
